import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useForm, useFormContext } from "react-hook-form";
import omit from "lodash/omit";
import { useRouter, useSearch } from "@tanstack/react-router";
import { ArrowLeft, DotIcon, InfoIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  useCreateFamily,
  useDistributorEmployees,
  useEmployeeRoles,
  useFamily,
  useUpdateFamily,
} from "creso-core/hooks";
import { Client as ClientApi } from "creso-core/api";
import {
  Client,
  ClientHoldingPattern,
  CreateFamilyMemberPayload,
  CreateFamilyPayload,
  FamilyAccessRole,
  FamilyDetail,
  UpdateFamilyPayload,
} from "creso-core/types";
import {
  FAMILY_CLIENT_STATES,
  JOINT_CLIENT_HOLDING_PATTERNS,
  TAX_STATUS_FILTER_LABEL_MAP,
} from "creso-core/constants";
import { getInitials } from "creso-core/utils";
import { handleError } from "@/utils/error";
import { Route } from "@/routes/__root";
import {
  Badge,
  Button,
  cn,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  MultiStepFormV2,
  type MultiStepFormV2Config,
  ScrollArea,
  Separator,
  ToggleGroup,
  ToggleGroupItem,
  V2Dialog,
  V2Label,
  VisuallyHidden,
} from "web-design-system";
import {
  ClientSearchSelect,
  Avatar,
  AvatarFamily,
  HoldingPatternBadge,
} from "web-core";
import { imageAssetUrl } from "@/utils/url";
import {
  AssignEmployeeSelect,
  SELECT_NO_EMPLOYEE_SENTINEL,
} from "../clients/assign-employee-select";

export type CreateFamilyMember = {
  id: number;
  name: string;
  phone_number?: string;
  tax_status?: string;
  family_name?: string | null;
  isPrimary?: boolean;
  client_id: number;
  holding_pattern?: ClientHoldingPattern;
};

export type FamilyAccess = "self" | "family";

export type CreateFamilyFormValues = {
  members: CreateFamilyMember[];
  family_name: string;
  assigned_to?: string;
  access: Record<string, FamilyAccess>;
};

export type FamilyDialogMode = "create" | "modify";

type FamilyDialogContextValue = {
  submit: () => void;
  isSubmitting: boolean;
  mode: FamilyDialogMode;
};

const FamilyDialogContext = createContext<FamilyDialogContextValue | null>(
  null
);

function useFamilyDialogContext() {
  const ctx = useContext(FamilyDialogContext);
  if (!ctx) {
    throw new Error(
      "useFamilyDialogContext must be used within FamilyDialogContext"
    );
  }
  return ctx;
}

export function getMemberGroupKey(member: CreateFamilyMember) {
  return member.phone_number?.trim() || `member-${member.id}`;
}

export function toAccessRole(access: FamilyAccess): FamilyAccessRole {
  return access === "family" ? "FULL" : "SELF";
}

/** `undefined` (not yet chosen) and the sentinel ("explicitly no one") both mean "don't send assigned_to". */
function resolveAssignedTo(value: string | undefined): number | undefined {
  if (!value || value === SELECT_NO_EMPLOYEE_SENTINEL) return undefined;
  return Number(value);
}

function buildUpdateFamilyPayload(
  values: CreateFamilyFormValues,
  originalFamily: FamilyDetail
): UpdateFamilyPayload {
  const access = values.access ?? {};
  const originalMembers = originalFamily.members ?? [];
  const originalByClientId = new Map(
    originalMembers.map((member) => [member.client_id, member])
  );
  const currentClientIds = new Set(values.members.map((member) => member.id));

  const remove_members = originalMembers
    .filter((member) => !currentClientIds.has(member.client_id))
    .map((member) => member.client_id);

  const add_members = values.members
    .filter((member) => !originalByClientId.has(member.id))
    .map((member) => ({
      client_id: member.client_id,
      access_role: toAccessRole(access[getMemberGroupKey(member)] ?? "self"),
    }));

  const set_roles = values.members.flatMap((member) => {
    const original = originalByClientId.get(member.id);
    if (!original) return [];

    const accessRole = toAccessRole(
      access[getMemberGroupKey(member)] ?? "self"
    );
    if (original.access_role === accessRole) return [];

    return [
      {
        member_id: original.family_member_id,
        access_role: accessRole,
      },
    ];
  });

  const name = values.family_name.trim();
  const assignedTo = resolveAssignedTo(values.assigned_to);
  const payload: UpdateFamilyPayload = {
    name,
    ...(assignedTo != null ? { assigned_to: assignedTo } : {}),
  };

  if (add_members.length) {
    payload.add_members = add_members;
    payload.confirm_move = true;
  }
  if (remove_members.length) {
    payload.remove_members = remove_members;
  }
  if (set_roles.length) {
    payload.set_roles = set_roles;
  }

  return payload;
}

function familyToFormValues(family: FamilyDetail): CreateFamilyFormValues {
  const members = (family.members ?? []).map((member, index, all) => {
    const phone = member.phone_number?.trim();
    const isFirstWithPhone = phone
      ? all.findIndex((item) => item.phone_number?.trim() === phone) === index
      : index === 0;

    return {
      id: member.client_id,
      name: member.display_name ?? member.name,
      phone_number: member.phone_number,
      isPrimary: isFirstWithPhone,
      client_id: member.client_id,
    };
  });

  const access: CreateFamilyFormValues["access"] = {};
  (family.members ?? []).forEach((member) => {
    const key = getMemberGroupKey({
      id: member.client_id,
      name: member.name,
      phone_number: member.phone_number,
      client_id: member.client_id,
    });
    if (!(key in access)) {
      access[key] = member.access_role === "FULL" ? "family" : "self";
    }
  });

  return {
    members,
    family_name: family.name,
    assigned_to:
      family.employee_id != null
        ? String(family.employee_id)
        : SELECT_NO_EMPLOYEE_SENTINEL,
    access,
  };
}
function buildCreateFamilyPayload(
  values: CreateFamilyFormValues
): CreateFamilyPayload {
  const access = values.access ?? {};
  const assignedTo = resolveAssignedTo(values.assigned_to);

  return {
    name: values.family_name.trim(),
    ...(assignedTo != null ? { assigned_to: assignedTo } : {}),
    confirm_move: true,
    members: values.members.map((member) => ({
      client_id: member.client_id,
      access_role: toAccessRole(access[getMemberGroupKey(member)] ?? "self"),
    })) satisfies CreateFamilyMemberPayload[],
  };
}

function getTaxLabel(taxStatus?: string) {
  if (!taxStatus) return undefined;
  return TAX_STATUS_FILTER_LABEL_MAP[taxStatus];
}

function getSubAccountLabel(member: CreateFamilyMember) {
  const label = getTaxLabel(member.tax_status);
  return label === "Minor" || label === "HUF" ? label : undefined;
}

function isSubAccount(member: CreateFamilyMember) {
  return Boolean(getSubAccountLabel(member));
}

export type MemberGroup = {
  key: string;
  primary: CreateFamilyMember;
  subAccounts: CreateFamilyMember[];
};

export function groupMembers(members: CreateFamilyMember[]): MemberGroup[] {
  const map = new Map<string, CreateFamilyMember[]>();
  members.forEach((member) => {
    const key = getMemberGroupKey(member);
    const existing = map.get(key) ?? [];
    existing.push(member);
    map.set(key, existing);
  });

  return Array.from(map.entries()).map(([key, groupedMembers]) => {
    const primary =
      groupedMembers.find((member) => member.isPrimary) ??
      [...groupedMembers].sort(
        (a, b) => Number(isSubAccount(a)) - Number(isSubAccount(b))
      )[0];
    const subAccounts = groupedMembers.filter(
      (member) => member.id !== primary.id
    );

    return { key, primary, subAccounts };
  });
}

function clientToMember(client: Client, isPrimary = false): CreateFamilyMember {
  return {
    id: client.id,
    name: client.name,
    phone_number: client.phone_number,
    tax_status: client.tax_status,
    family_name: client.family_name,
    isPrimary,
    client_id: client.id,
    holding_pattern: client.holding_pattern,
  };
}

function FamilyMoveWarning({ group }: { group: MemberGroup }) {
  const familyName = [group.primary, ...group.subAccounts]
    .map((member) => member.family_name?.trim())
    .find(Boolean);

  if (!familyName) return null;

  const otherCount = group.subAccounts.length;

  return (
    <div className="flex items-start gap-2 text-xs text-tag-yellow-foreground">
      <InfoIcon className="mt-0.5 size-4 shrink-0" />
      <span className="text-xs">
        Part of <span className="font-medium">{familyName}</span>
        {otherCount > 0 ? (
          <>
            {" "}
            with {otherCount} other account{otherCount === 1 ? "" : "s"}
          </>
        ) : null}
        , adding them here will remove them from it.
      </span>
    </div>
  );
}

export function FamilyMemberGroupCard({
  group,
  onRemove,
  headerAction,
  groupAccess,
  children,
  step,
}: {
  group: MemberGroup;
  onRemove?: () => void;
  headerAction?: React.ReactNode;
  groupAccess?: FamilyAccess;
  children?: React.ReactNode;
  step?: "create-family" | "configure-access";
}) {
  return (
    <div
      className={cn(
        "flex flex-col p-4 gap-4",
        step === "create-family" && "rounded-xl bg-card",
        step === "configure-access" && "border-b border-border pt-0"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Avatar
            fallback={getInitials(group.primary.name)}
            size="sm"
            url={""}
          />
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{group.primary.name}</span>
              {groupAccess ? (
                <Badge
                  variant="custom"
                  className={cn(
                    groupAccess === "family"
                      ? "bg-tag-violet text-tag-violet-foreground"
                      : "bg-tag-blue text-tag-blue-foreground"
                  )}
                >
                  {groupAccess === "family" ? "Full" : "Self"}
                </Badge>
              ) : null}
              {group.primary.holding_pattern &&
              JOINT_CLIENT_HOLDING_PATTERNS.includes(
                group.primary.holding_pattern
              ) ? (
                <HoldingPatternBadge
                  holdingPattern={group.primary.holding_pattern}
                />
              ) : null}
            </div>
            {group.primary.phone_number ? (
              <span className="text-xs text-muted-foreground">
                {group.primary.phone_number}
              </span>
            ) : null}
          </div>
        </div>
        {headerAction ??
          (onRemove ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8 shrink-0 text-muted-foreground"
              onClick={onRemove}
            >
              <X className="size-4" />
            </Button>
          ) : null)}
      </div>

      {group.subAccounts.length > 0 ? (
        <div className="ml-6 pl-4 flex flex-col gap-4">
          {group.subAccounts.map((subAccount) => (
            <div key={subAccount.id} className="flex items-center gap-3">
              <Avatar
                url=""
                fallback={getInitials(subAccount.name)}
                size="sm"
              />
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <p className="truncate text-sm font-medium text-foreground">
                  {subAccount.name}
                </p>
                {getSubAccountLabel(subAccount) ? (
                  <Badge
                    variant="custom"
                    className="shrink-0 bg-secondary text-secondary-foreground"
                  >
                    {getSubAccountLabel(subAccount)}
                  </Badge>
                ) : null}
                {subAccount.holding_pattern &&
                JOINT_CLIENT_HOLDING_PATTERNS.includes(
                  subAccount.holding_pattern
                ) ? (
                  <HoldingPatternBadge
                    holdingPattern={subAccount.holding_pattern}
                  />
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {step === "create-family" ? <FamilyMoveWarning group={group} /> : null}

      {children}
    </div>
  );
}

function CreateFamilyStep({ next }: { next: () => void }) {
  const { mode } = useFamilyDialogContext();
  const form = useFormContext<CreateFamilyFormValues>();
  const members = form.watch("members");
  const access = form.watch("access") ?? {};
  const [isAddingMember, setIsAddingMember] = useState(false);
  const isModify = mode === "modify";

  const groups = useMemo(() => groupMembers(members), [members]);

  const addMember = useCallback(
    async (client: Client) => {
      if (!client.phone_number?.trim()) {
        toast.error("Client does not have a phone number");
        return;
      }

      if (!client.investment_account) {
        toast.error("Client does not have an investment account");
        return;
      }

      const current = form.getValues("members");
      if (current.some((member) => member.id === client.id)) return;

      setIsAddingMember(true);
      try {
        const response = await ClientApi.getAllClients(1, 50, "", {
          phone_number: client.phone_number,
        });
        const relatedClients = response.results.filter(
          (related: Client) => related.investment_account
        );

        const existingIds = new Set(current.map((member) => member.id));
        const newMembers = relatedClients
          .filter((related: Client) => !existingIds.has(related.id))
          .map((related: Client) =>
            clientToMember(related, related.id === client.id)
          );

        if (
          !relatedClients.some((related: Client) => related.id === client.id) &&
          !existingIds.has(client.id)
        ) {
          newMembers.unshift(clientToMember(client, true));
        }

        if (newMembers.length === 0) return;

        form.setValue("members", [...current, ...newMembers], {
          shouldDirty: true,
        });
      } catch (error) {
        handleError(error, "Failed to fetch related accounts");
      } finally {
        setIsAddingMember(false);
      }
    },
    [form]
  );

  const removeGroup = useCallback(
    (groupKey: string) => {
      form.setValue(
        "members",
        form
          .getValues("members")
          .filter((member) => getMemberGroupKey(member) !== groupKey),
        { shouldDirty: true }
      );
    },
    [form]
  );

  const selectedIds = members.map((member) => member.id);

  const goToAccessStep = useCallback(() => {
    const familyName = form.getValues("family_name").trim();
    const firstMemberName = groups[0]?.primary.name;
    if (!familyName && firstMemberName) {
      form.setValue(
        "family_name",
        `${firstMemberName.split(" ")[0]}'s Family`,
        { shouldDirty: true }
      );
    }
    next();
  }, [form, groups, next]);

  return (
    <div className="flex size-full flex-col">
      <div className="flex flex-col gap-2 p-6 pb-0">
        <DialogTitle className="!text-lg font-semibold">
          {isModify ? "Add or Remove" : "Create Family"}
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          Accounts using same phone number are considered as family.
        </DialogDescription>
      </div>
      <Separator className="my-5" />
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-5 p-6 pt-0">
          <ClientSearchSelect
            clientsFilter={{ status: FAMILY_CLIENT_STATES }}
            placeholder="Add clients"
            excludeIds={selectedIds}
            onSelect={addMember}
            autoFocus={!isModify}
          />
          {isAddingMember ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Fetching related accounts...
            </div>
          ) : null}
          {groups.length > 0 ? (
            <div className="flex flex-col gap-5">
              {groups.map((group) => (
                <FamilyMemberGroupCard
                  key={group.key}
                  group={group}
                  onRemove={() => removeGroup(group.key)}
                  groupAccess={
                    isModify ? (access[group.key] ?? "self") : undefined
                  }
                  step={"create-family"}
                />
              ))}
            </div>
          ) : null}
        </div>
      </ScrollArea>
      <Separator className="my-5" />
      <div className="flex items-center justify-end p-6 pt-0">
        <Button
          type="button"
          onClick={goToAccessStep}
          disabled={members.length === 0}
        >
          {isModify ? "Next" : "Create Family"}
        </Button>
      </div>
    </div>
  );
}

export function AccessToggle({
  value,
  onChange,
}: {
  value: FamilyAccess;
  onChange: (value: FamilyAccess) => void;
}) {
  return (
    <ToggleGroup
      type="single"
      size="xs"
      value={value}
      onValueChange={(next) => {
        if (next) onChange(next as FamilyAccess);
      }}
    >
      <ToggleGroupItem value="self">Self</ToggleGroupItem>
      <ToggleGroupItem value="family">Family</ToggleGroupItem>
    </ToggleGroup>
  );
}

function ConfigureAccessStep({ prev }: { prev: () => void }) {
  const { submit, isSubmitting, mode } = useFamilyDialogContext();
  const form = useFormContext<CreateFamilyFormValues>();
  const isModify = mode === "modify";

  const members = form.watch("members");
  const access = form.watch("access") ?? {};

  const { data: employeesData } = useDistributorEmployees({ status: "active" });
  const employees = employeesData?.results ?? [];
  const { data: employeeRoles } = useEmployeeRoles();
  const roleIdToName = useMemo(() => {
    const map = new Map<number, string>();
    employeeRoles?.results?.forEach((role) => {
      map.set(Number(role.id), role.name);
    });
    return map;
  }, [employeeRoles?.results]);

  const assignedTo = form.watch("assigned_to");
  const assignedEmployeeName =
    assignedTo === SELECT_NO_EMPLOYEE_SENTINEL
      ? "No one"
      : employees.find((employee) => String(employee.id) === assignedTo)?.name;

  const groups = useMemo(() => groupMembers(members), [members]);

  const setAccess = useCallback(
    (key: string, value: FamilyAccess) => {
      form.setValue(`access.${key}`, value, { shouldDirty: true });
    },
    [form]
  );

  return (
    <div className="flex size-full flex-col">
      <DialogTitle className="!text-lg font-semibold p-6 pb-0">
        {isModify ? "Update Access" : "Configure Access"}
      </DialogTitle>
      <Separator className="my-5" />
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-5 p-6 pt-0">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center pt-1">
            <div className="flex flex-1 items-center gap-4">
              <V2Label htmlFor="family-name" className="shrink-0 font-normal">
                Family Name:
              </V2Label>
              <Input
                id="family-name"
                value={form.watch("family_name")}
                onChange={(event) =>
                  form.setValue("family_name", event.target.value, {
                    shouldDirty: true,
                  })
                }
                placeholder="Enter family name"
              />
            </div>
            {employees.length > 0 ? (
              <div className="flex flex-1 items-center gap-4">
                <V2Label className="shrink-0 font-normal">Assign to:</V2Label>
                <AssignEmployeeSelect
                  employees={employees}
                  roleIdToName={roleIdToName}
                  assignedCountKind="family"
                  value={assignedTo}
                  onValueChange={(value) =>
                    form.setValue("assigned_to", value, { shouldDirty: true })
                  }
                  triggerClassName="flex-1 h-9 text-left"
                  triggerLabel={assignedEmployeeName ?? "Select employee"}
                  triggerTextClassName={cn(
                    "text-sm line-clamp-1",
                    !assignedEmployeeName && "text-muted-foreground"
                  )}
                />
              </div>
            ) : null}
          </div>

          <Separator />

          <ul className="flex flex-col gap-3 text-sm text-foreground">
            <li className="flex items-center gap-2">
              <DotIcon className="text-foreground size-5" />
              <span className="font-semibold">Self View:</span>
              <span>Accesses accounts made using their phone number.</span>
            </li>
            <li className="flex items-center gap-2">
              <DotIcon className="text-foreground size-5" />
              <span className="font-semibold">Family view:</span>
              <span className="">Access to all portfolios of the family.</span>
            </li>
          </ul>

          <Separator />

          <div className="flex flex-col gap-5">
            {groups.map((group) => {
              const groupAccess = access[group.key] ?? "self";
              return (
                <FamilyMemberGroupCard
                  key={group.key}
                  group={group}
                  headerAction={
                    <AccessToggle
                      value={groupAccess}
                      onChange={(value) => setAccess(group.key, value)}
                    />
                  }
                  step={"configure-access"}
                />
              );
            })}
          </div>
        </div>
      </ScrollArea>
      <Separator className="my-5" />
      <div className="flex items-center justify-between p-6 pt-0">
        <Button type="button" variant="outline" onClick={prev}>
          <ArrowLeft className="size-4" />
          Back
        </Button>
        <Button type="button" onClick={submit} disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : null}
          {isModify ? "Save Changes" : "Notify Clients"}
        </Button>
      </div>
    </div>
  );
}

export function CreateFamilyDialog() {
  const router = useRouter();
  const search = useSearch({ from: Route.id });
  const mode: FamilyDialogMode =
    search.modal === "modify-family" ? "modify" : "create";
  const familyId = mode === "modify" ? search.family_id : undefined;
  const isModify = mode === "modify";

  const { data: family, isLoading: isFamilyLoading } = useFamily(
    familyId ?? 0,
    isModify && !!familyId
  );
  const { mutateAsync: createFamily, isPending: isCreating } =
    useCreateFamily();
  const { mutateAsync: updateFamily, isPending: isUpdating } = useUpdateFamily(
    familyId ?? 0
  );
  const isSubmitting = isModify ? isUpdating : isCreating;

  const form = useForm<CreateFamilyFormValues>({
    defaultValues: {
      members: [],
      family_name: "",
      assigned_to: undefined,
      access: {},
    },
  });

  useEffect(() => {
    if (!isModify || !family) return;
    form.reset(familyToFormValues(family));
  }, [family, form, isModify]);

  const close = useCallback(() => {
    router.history.back();
  }, [router]);

  const submit = useCallback(async () => {
    const values = form.getValues();
    const familyName = values.family_name.trim();

    if (!familyName) {
      toast.error("Family name is required");
      return;
    }

    try {
      if (isModify && familyId) {
        if (!family) {
          toast.error("Family details are still loading");
          return;
        }
        await updateFamily(buildUpdateFamilyPayload(values, family));
        await router.invalidate();
        toast.success("Family updated");
        router.navigate({
          to: "/clients/families/$id",
          params: { id: String(familyId) },
          search: (prev) => omit(prev, ["modal", "family_id"]),
          replace: true,
        });
        return;
      }

      const createdFamily = await createFamily(
        buildCreateFamilyPayload(values)
      );
      toast.success("Family created");
      router.navigate({
        to: "/clients/families/$id",
        params: { id: String(createdFamily.id) },
        search: (prev) => omit(prev, ["modal"]),
        replace: true,
      });
    } catch (error) {
      handleError(
        error,
        isModify ? "Failed to update family" : "Failed to create family"
      );
    }
  }, [createFamily, family, familyId, form, isModify, router, updateFamily]);

  const dialogConfig = useMemo<MultiStepFormV2Config<CreateFamilyFormValues>[]>(
    () => [
      {
        id: "create-family",
        label: () => (isModify ? "Add or Remove" : "Create Family"),
        component: CreateFamilyStep,
        isNavigable: () => true,
        isComplete: (values) => values.members.length > 0,
      },
      {
        id: "configure-access",
        label: () => (isModify ? "Update Access" : "Configure Access"),
        component: ConfigureAccessStep,
        isNavigable: (values) => values.members.length > 0,
      },
    ],
    [isModify]
  );

  if (isModify && !familyId) return null;

  const modalTitle = isModify ? "Manage family" : "Family Setup";
  const isLoading = isModify && isFamilyLoading;

  return (
    <V2Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          if (isModify) event.preventDefault();
        }}
        className="flex h-170 max-w-[960px] w-[60vw] flex-col overflow-hidden p-0"
      >
        <VisuallyHidden>
          <DialogTitle>{modalTitle}</DialogTitle>
          <DialogDescription>
            {isModify
              ? "Manage family members and access."
              : "Create a new family of clients."}
          </DialogDescription>
        </VisuallyHidden>
        <FamilyDialogContext.Provider value={{ submit, isSubmitting, mode }}>
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center p-12">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <MultiStepFormV2
              title={
                <div className="flex flex-col items-start gap-2">
                  <AvatarFamily
                    fallback="Family"
                    url={imageAssetUrl("avatar-family.svg")}
                    variant="gradient"
                    size="lg"
                  />
                  {modalTitle}
                </div>
              }
              config={dialogConfig}
              defaultStep={0}
              form={form}
              onComplete={submit}
            />
          )}
        </FamilyDialogContext.Provider>
      </DialogContent>
    </V2Dialog>
  );
}
